import * as Sentry from '@sentry/react';
import { UserManager, UserManagerSettings } from 'oidc-client-ts';
import { API_URL, getOAuthAccessToken } from '../api/api';

// Our Google OAuth client is confidential (requires a client_secret for the
// authorization_code/refresh_token exchange). That secret can never live in
// browser-bundled code, so instead of letting oidc-client-ts POST straight to
// Google's token endpoint, we point it at our own backend, which injects the
// secret server-side and forwards the request/response through unchanged.
// Only token_endpoint is overridden; every other endpoint still comes from
// Google's normal OIDC discovery document.

// Calendar connect only ever runs once the user is signed in, so its token
// exchange goes through the authenticated calendar-auth route (mirrors
// CalendarApi.exchangeGoogleCalendarOAuthToken in
// src/data-access/api/openapi/api.ts, generated from lifestack-backend's
// POST /api/v1/calendar/auth/google/token). The Authorization header is a
// function so it's read fresh at request time, not at module load.
//
// extraHeaders is applied to every request oidc-client-ts makes, including the
// discovery fetch to accounts.google.com, whose CORS preflight rejects the
// Authorization header. So the full metadata is supplied statically here
// (instead of metadataSeed) to skip discovery entirely.
const calSettings: UserManagerSettings = {
  authority: 'https://accounts.google.com',
  metadata: {
    issuer: 'https://accounts.google.com',
    authorization_endpoint: 'https://accounts.google.com/o/oauth2/v2/auth',
    token_endpoint: `${API_URL}/api/v1/calendar/auth/google/token?generatedClientType=web`,
    userinfo_endpoint: 'https://openidconnect.googleapis.com/v1/userinfo',
    jwks_uri: 'https://www.googleapis.com/oauth2/v3/certs',
    revocation_endpoint: 'https://oauth2.googleapis.com/revoke',
  },
  extraHeaders: {
    Authorization: () => `Bearer ${getOAuthAccessToken() || ''}`,
  },
  client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
  redirect_uri: `${window.location.origin}/callback/google`,
  response_type: 'code',
  scope:
    'openid email https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/calendar.readonly',
  automaticSilentRenew: true,
  extraQueryParams: { access_type: 'offline', prompt: 'consent' },
};

// Sign-in runs before the user has an account or a JWT, so it has to use the
// public account-auth route instead (mirrors
// AccountApi.exchangeGoogleOAuthToken, generated from lifestack-backend's
// POST /api/v1/account/auth/google/token).
const authSettings: UserManagerSettings = {
  authority: 'https://accounts.google.com',
  metadataSeed: {
    token_endpoint: `${API_URL}/api/v1/account/auth/google/token?generatedClientType=web`,
  },
  client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
  redirect_uri: `${window.location.origin}/callback/google-auth`,
  popup_redirect_uri: `${window.location.origin}/callback/google-auth`,
  response_type: 'code',
  scope: 'openid email profile',
  automaticSilentRenew: true,
  extraQueryParams: { access_type: 'offline', prompt: 'consent' },
};

export type GoogleAuthFlow = 'calendar_connect' | 'sign_in';

// oidc-client-ts surfaces failures as ErrorResponse (OAuth error from the token
// proxy / Google) or plain Errors. Tag them so Sentry can group and filter by flow.
export const captureGoogleAuthError = (
  error: unknown,
  flow: GoogleAuthFlow,
  stage: string
): void => {
  const oauthError = error as { error?: string; error_description?: string; status?: number };
  if (oauthError?.error === 'access_denied') {
    return;
  }

  Sentry.withScope((scope) => {
    scope.setTag('google_auth_flow', flow);
    scope.setTag('google_auth_stage', stage);
    scope.setContext('google_auth', {
      oauthError: oauthError?.error,
      oauthErrorDescription: oauthError?.error_description,
      status: oauthError?.status,
    });
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
  });
};

export const googleCalAuth = new UserManager(calSettings);
export const googleAuth = new UserManager(authSettings);

// Silent renew runs in the background with no UI, so without this hook a failing
// refresh_token exchange would never be seen.
googleCalAuth.events.addSilentRenewError((error) =>
  captureGoogleAuthError(error, 'calendar_connect', 'silent_renew')
);
googleAuth.events.addSilentRenewError((error) =>
  captureGoogleAuthError(error, 'sign_in', 'silent_renew')
);
